import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Smile, Euro, Clock, Shield } from 'lucide-react';

function MetricBar({ name, score, maxScore, color, weight, icon, tooltip }) {
  const [width, setWidth] = useState(0);
  const percentage = (score / maxScore) * 100;

  useEffect(() => {
    setTimeout(() => setWidth(percentage), 100);
  }, [percentage]);

  const colorClasses = {
    blue: {
      bg: 'from-blue-500 to-blue-400',
      iconBg: 'bg-blue-500/20',
      iconColor: 'text-blue-400',
    },
    emerald: {
      bg: 'from-emerald-500 to-emerald-400',
      iconBg: 'bg-emerald-500/20',
      iconColor: 'text-emerald-400',
    },
    amber: {
      bg: 'from-amber-500 to-amber-400',
      iconBg: 'bg-amber-500/20',
      iconColor: 'text-amber-400',
    },
    violet: {
      bg: 'from-violet-500 to-violet-400',
      iconBg: 'bg-violet-500/20',
      iconColor: 'text-violet-400',
    },
  };

  const config = colorClasses[color] || colorClasses.blue;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.8 }}
      className="bg-slate-900 border border-white/10 rounded-xl p-6 hover:bg-slate-800 transition-colors group"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg ${config.iconBg} flex items-center justify-center`}>
            <div className={config.iconColor}>{icon}</div>
          </div>
          <span className="text-sm font-medium text-gray-400 uppercase tracking-wider">
            {name}
          </span>
        </div>
        <span className="text-xs text-gray-500">{weight}%</span>
      </div>

      {/* Score */}
      <div className="flex items-baseline gap-2 mb-3">
        <span className="text-3xl font-bold text-gray-50">{score.toFixed(1)}</span>
        <span className="text-xl text-gray-500">/ {maxScore}</span>
        <span
          className={`text-sm font-semibold ml-auto ${
            percentage >= 80 ? 'text-emerald-400' : percentage >= 60 ? 'text-amber-400' : 'text-red-400'
          }`}
        >
          {percentage.toFixed(0)}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${width}%` }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className={`h-full bg-gradient-to-r ${config.bg} transition-all duration-1000 ease-out`}
        />
      </div>

      {/* Tooltip */}
      <div className="mt-2 text-xs text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">
        {tooltip}
      </div>
    </motion.div>
  );
}

export default function MetricsBreakdown({ breakdown }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
      <MetricBar
        name="Customer Satisfaction"
        score={breakdown.customer_satisfaction}
        maxScore={40}
        color="blue"
        weight={40}
        icon={<Smile className="w-4 h-4" />}
        tooltip={`Average CSAT across all customers: ${((breakdown.customer_satisfaction / 40) * 100).toFixed(0)}%`}
      />
      <MetricBar
        name="Cost Efficiency"
        score={breakdown.cost_efficiency}
        maxScore={30}
        color="emerald"
        weight={30}
        icon={<Euro className="w-4 h-4" />}
        tooltip="How well you managed the budget"
      />
      <MetricBar
        name="Response Time"
        score={breakdown.response_time}
        maxScore={20}
        color="amber"
        weight={20}
        icon={<Clock className="w-4 h-4" />}
        tooltip="Average time to resolve customer issues"
      />
      <MetricBar
        name="Protocol Adherence"
        score={breakdown.protocol_adherence}
        maxScore={10}
        color="violet"
        weight={10}
        icon={<Shield className="w-4 h-4" />}
        tooltip="Following proper procedures and protocols"
      />
    </div>
  );
}
